import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { map, omit } from 'ramda'
import {
  type BasePuiDialogProps,
  ButtonWithLoader,
  type Nil,
  PuiDialog,
} from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import {
  editVaccineHistory,
  saveVaccineHistory,
} from '~/store/actions/medicalHistory'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getMedicalHistoryIsSending } from '~/store/reducers/medicalHistory'
import { getPatient } from '~/store/reducers/patients'
import type { VaccineHistoryItem } from '~/types'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'

import RemindersSnapshot from '../../clients/snapshots-and-records/snapshots/RemindersSnapshot/RemindersSnapshot'
import PatientDetailsHeader from '../../soapV2/summary/patient-details/PatientDetailsHeader'
import VaccineHistoryExpandedDetails, {
  type VaccineHistoryExpandedDetailsHandle,
} from './VaccineHistoryExpandedDetails'
import VaccineListWithFilters from './VaccineListWithFilters'

const useStyles = makeStyles(
  (theme) => ({
    paper: {
      width: 976,
      maxWidth: 976,
      [theme.breakpoints.down('md')]: {
        margin: '48px auto !important',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 32px)',
      },
    },
    paperV2: {
      width: 1256,
      maxWidth: 1256,
      [theme.breakpoints.down('md')]: {
        margin: '48px auto !important',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 32px)',
      },
    },
    dialogContentRoot: {
      padding: theme.spacing(2),
    },
    button: {
      width: 150,
    },
  }),
  { name: 'VaccineHistoryDialog' },
)

export interface VaccineHistoryDialogProps extends BasePuiDialogProps {
  clientId: string | Nil
  patientId: string | Nil
  vaccine?: VaccineHistoryItem
}

const VaccineHistoryDialog = ({
  vaccine,
  patientId,
  clientId,
  open,
  onClose,
}: VaccineHistoryDialogProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const isSending = useSelector(getMedicalHistoryIsSending)
  const setCloseAfterCreationOn = useCloseAfterCreation(
    onClose,
    getMedicalHistoryIsSending,
  )
  const { t } = useTranslation(['Common', 'Dialogs'])
  const patient = useSelector(getPatient(patientId))

  const detailsRef = useRef<VaccineHistoryExpandedDetailsHandle>(null)

  const onProceed = (items: VaccineHistoryItem[]) => {
    const processedItems = map(omit(['id']), items)

    setCloseAfterCreationOn()
    if (patientId) {
      dispatch(saveVaccineHistory(processedItems, patientId, clientId))
    }
  }

  const isSimplifyReminderFlowForVCREnabled = useSelector(
    getFeatureToggle(FeatureToggle.SIMPLIFY_REMINDER_FLOW_FOR_VCR),
  )

  const save = () => {
    if (detailsRef.current?.validate()) {
      setCloseAfterCreationOn()
      if (patientId) {
        dispatch(editVaccineHistory([detailsRef.current.get()], patientId))
      }
    }
  }

  const gridProps = isSimplifyReminderFlowForVCREnabled ? { xs: 7 } : { xs: 12 }

  return (
    <PuiDialog
      actions={
        vaccine && (
          <ButtonWithLoader
            className={classes.button}
            disabled={isSending}
            loading={isSending}
            onClick={save}
          >
            {t('Common:SAVE_ACTION')}
          </ButtonWithLoader>
        )
      }
      aria-labelledby="vaccine-history-dialog"
      classes={{
        paper: isSimplifyReminderFlowForVCREnabled
          ? classes.paperV2
          : classes.paper,
        dialogContentRoot: classes.dialogContentRoot,
      }}
      open={open}
      title={
        vaccine
          ? t('Dialogs:VACCINE_HISTORY_DIALOG.TITLE_EDIT')
          : t('Dialogs:VACCINE_HISTORY_DIALOG.TITLE_ADD')
      }
      onClose={onClose}
    >
      <Grid container spacing={2}>
        <Grid item {...gridProps}>
          {vaccine ? (
            <VaccineHistoryExpandedDetails
              edit
              patientId={patientId ?? ''}
              ref={detailsRef}
              vaccine={vaccine}
            />
          ) : (
            <VaccineListWithFilters
              patientId={patientId}
              onProceed={onProceed}
            />
          )}
        </Grid>
        {isSimplifyReminderFlowForVCREnabled && (
          <Grid item xs={5}>
            <Grid container direction="column">
              <Grid item mb={1}>
                <PatientDetailsHeader
                  clientId={clientId ?? ''}
                  patient={patient}
                />
              </Grid>
              <Grid item>
                <RemindersSnapshot
                  canExpand={false}
                  clientId={clientId ?? ''}
                  name={t('Common:REMINDERS')}
                  patientId={patientId ?? ''}
                  showActions={false}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </PuiDialog>
  )
}

export default VaccineHistoryDialog
